/**
 * WhatsAppChatbot — web version
 *
 * Simple FAB that opens WhatsApp directly in a new tab, matching the mobile
 * app's behaviour (wa.me deep link, no in-app chat dialog).
 */
import React from 'react';
import { Fab, Tooltip } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useAuth } from '../../context/AuthContext';

const WHATSAPP_SUPPORT_NUMBER = '233551110949';

// Keep the event-based API so any existing callers still compile
export const openWhatsAppChatbot = () => {
  const event = new CustomEvent('openWhatsAppChatbot');
  window.dispatchEvent(event);
};

const WhatsAppChatbot: React.FC = () => {
  const { user } = useAuth();

  const handleClick = () => {
    const greeting = user
      ? `Hello Dwello Support! I'm ${user.firstName} ${user.lastName} and I need help.`
      : 'Hello Dwello Support! I need help.';
    const url = `https://wa.me/${WHATSAPP_SUPPORT_NUMBER}?text=${encodeURIComponent(greeting)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Also respond to external openWhatsAppChatbot events
  React.useEffect(() => {
    const handler = () => handleClick();
    window.addEventListener('openWhatsAppChatbot', handler);
    return () => window.removeEventListener('openWhatsAppChatbot', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <Tooltip title="Chat with Dwello Support on WhatsApp" placement="left">
      <Fab
        aria-label="WhatsApp Support"
        onClick={handleClick}
        sx={{
          position: 'fixed',
          bottom: 'calc(24px + env(safe-area-inset-bottom))',
          right: 'calc(24px + env(safe-area-inset-right))',
          zIndex: 1000,
          bgcolor: '#25D366',
          color: '#fff',
          '&:hover': { bgcolor: '#1ebe57' },
          width: 56,
          height: 56,
        }}
      >
        <WhatsAppIcon sx={{ fontSize: 28 }} />
      </Fab>
    </Tooltip>
  );
};

export default WhatsAppChatbot;
